<script lang="ts" setup>
  import { computed, ref } from "vue";
  import { watchDebounced } from "@vueuse/core";
  import { useRewardsStore } from "@/stores/rewards";
  import router from "@/router";
  import type { Reward } from "@/models/reward";
  import SearchModal from "@/components/modals/SearchModal.vue";

  const searchProps = defineProps<{
    label: string
  }>();

  const searchQuery = ref<string>('');
  const searchResults = ref<Reward[]>([]);
  const rewardsStore = useRewardsStore();
  const isSearchModalShowing = ref<boolean>(false);

  // Compute the search field label background color, depending on the current route
  // Use theme plugin for colors
  const labelBackground = computed(() => {
    switch(router.currentRoute.value.name) {
      case 'home':
        return 'rgb(var(--v-theme-background))';
      default:
        return 'rgb(var(--v-theme-neutral-100))';
    }
  });

  // Compute whether to render the search results menu
  const renderMenu = computed(() => {
    return window.innerWidth > 1280 && searchResults.value.length > 0;
  });

  // Watch for changes to the search query on a debounce
  watchDebounced(
    searchQuery,
    async (value) => {
      if (value.length > 0) {
        await rewardsStore.fetchSearchRewards(value);
        searchResults.value = rewardsStore.getSearchResults();
      } else {
        searchResults.value = [];
      }
    },
    {debounce: 250, maxWait: 500}
  );

  // Update route history, reward store and search input (with search query)
  function selectResult(query: string) {
    router.push({ name: 'rewards', query: { search: encodeURIComponent(query) }, force: true });
    rewardsStore.setSearchQuery(query);
    searchQuery.value = query;
  }

  // Update route history, reward store and search input (no search query)
  function seeAllRewards() {
    router.push({ name: 'rewards', query: { search: undefined }, force: true });
    rewardsStore.setSearchQuery('');
    searchQuery.value = '';
  }

  function openSearchModal(searchQuery: string): void {
    if (window.innerWidth > 1280) {
      selectResult(searchQuery);
      return;
    }

    isSearchModalShowing.value = true;
  }
</script>

<template>
  <div class="search-field">
    <v-menu
      location="bottom"
      class="search-menu"
    >
      <template #activator="{ props }">
        <v-text-field
          :label="searchProps.label"
          variant="plain"
          append-icon="search"
          @click:append="openSearchModal(searchQuery)"
          v-model="searchQuery"
          v-bind="props"
          v-on:keyup.enter="selectResult(searchQuery)"
        />
      </template>

      <v-card
        min-width="230"
        v-if="renderMenu"
      >
        <v-list-item class="see-all-offers">
          <div @click="seeAllRewards()">
            {{ $t('nav.all-offers') }}
          </div>
        </v-list-item>
        <v-list
          v-for="(obj, n) in searchResults"
          :key="n"
        >
          <v-list-item>
            <div class="result" @click="selectResult(obj.brand)">
              {{ obj.brand }}
            </div>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
  <SearchModal
    v-model="isSearchModalShowing"
    @close="isSearchModalShowing = false"
  />
</template>

<style lang="scss" scoped>

  :deep(.v-card) {
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: getFontSize(body-2);
  }

  :deep(.v-list-item--density-default.v-list-item--one-line) {
    min-height: 10px;
    color: rgb(var(--v-theme-neutral-400));
  }

  :deep(.v-list--one-line) {
    padding: 4px 0;
    background-color: transparent;
    transition: 0.5s ease background-color;
  }

  :deep(.v-list--one-line):hover {
    background-color: rgb(var(--v-theme-neutral-100));
  }

  .result {
    display: block;
    width: 100%;
  }

  .result:hover {
    color: inherit;
    cursor: pointer;
  }

  .see-all-offers {
    font-weight: getFontWeight(bold);
    color: rgb(var(--v-theme-primary));
    transition: 0.5s ease background-color;

    &:hover {
      color: rgb(var(--v-theme-primary));
      cursor: pointer;
    }
  }

  .see-all-offers:hover {
    background-color: rgb(var(--v-theme-neutral-100));
  }

  .search-field {

    @media(max-width: breakpoint(lg) - 1px) {
      :deep(.v-label), :deep(.v-field) {
        display: none;
      }

      :deep(.v-input__append) {
        margin-right: 1rem;
      }
    }

    @media(min-width: breakpoint(lg)) {
      .v-input {
        border: 1px solid #E4E8F0;
        border-radius: 36px;
        height: 45px;
        padding-left: 30px;
        padding-right: 30px;

        :deep(.v-label) {
          transition: 0.15s ease top;
          color: rgb(var(--v-theme-neutral-400));
        }

        :deep(.v-field:not(.v-field--focused):not(.v-field--active) .v-label.v-field-label) {
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
        }

        :deep(.v-field.v-field--focused .v-label.v-field-label), :deep(.v-field.v-field--active .v-label.v-field-label) {
          top: 0;
          transform: translateY(-55%);
          background-color: v-bind(labelBackground);
          padding-left: 4px;
          padding-right: 4px;
        }

        :deep(.v-field__field) {
          height: 45px !important;
        }

        :deep(.v-field__input) {
          padding: 0;
        }

        :deep(.v-input__append) {
          padding-top: 10px;
        }
      }
    }
  }
</style>